import * as React from 'react';
import {
	Modal,
	Button,
	Grid,
	XFilledIcon
} from 'react-unity';
import './RuleModal.css';
import TextAreaField from '../../../common/form-controls/TextareaField';
import ToggleField from '../../../common/form-controls/ToggleField';
import TextInputField from '../../../common/form-controls/TextInputField';
import {AbstractRulesForm, IRuleStateForm, RulesFormProps} from './AbstractRulesForm';
import {RuleAction} from '../../../../models/enums/POR/RuleAction';
import {TrafficDirection} from '../../../../models/enums/POR/TrafficDirection';
import SelectField from '../../../common/form-controls/SelectField';
import {RuleRegion} from '../../../../models/enums/POR/RuleRegion';
import {RuleOperation} from '../../../../models/enums/POR/RuleOperation';
import {PortOpeningRequestsDirection} from '../../../../models/enums/POR/PortOpeningRequestsDirection';
import DatePickerField from '../../../common/form-controls/DatePickerField';
import moment from 'moment';
import MultipleSelectField from '../../../common/form-controls/MultipleSelectField';
import SubscriptionRequest from '../../../../models/entities/SubscriptionRequest';
import SubscriptionRequestsService from '../../../../services/SubscriptionRequestsService';
import {VirtualNetwork} from '../../../../models/entities/AzureResources/VirtualNetwork';

interface RuleModalSubToOnPremProps extends RulesFormProps{
	subscriptionRequestId: number;
	porDirection?: PortOpeningRequestsDirection;
	destinationSubToSpoke?: SubscriptionRequest;
	vNetsToSpoke?: VirtualNetwork[];

}

interface SubToOnPremRuleStateForm extends IRuleStateForm {
	name: string;
    trafficDirection: TrafficDirection;
    protocol: string;
}
interface RuleModalSubToOnPremState {
	stateForm: SubToOnPremRuleStateForm;
}

export default class RuleModalSubToOnPrem extends AbstractRulesForm<
	RuleModalSubToOnPremProps,
	RuleModalSubToOnPremState
> {
	subscriptionRequestsService: SubscriptionRequestsService;

	constructor(props: RuleModalSubToOnPremProps) {
		super(props);
		this.state = {
			stateForm: this.props.isFromHistory ? this.getRuleCopy() : this.getDefaultForm(),
		};
		this.subscriptionRequestsService = new SubscriptionRequestsService();
		this.initFormModel();
	}

	componentDidUpdate(prevProps: RuleModalSubToOnPremProps) {
		super.componentDidUpdate(prevProps);

		if (this.props.porDirection === PortOpeningRequestsDirection.SubscriptionToSubscription) // No Traffic Direction Validations for rules of SubToSub POR
			return; 
		const justOpened = !prevProps.visible && this.props.visible;
		const ruleChanged = this.props.editingRule !== prevProps.editingRule && this.props.visible;

		if (justOpened || ruleChanged) {
			const value = this.props.editingRule?.trafficDirection?.value ?? TrafficDirection.In.value;
			this.initTrafficDirectionValidations(value);
		}
	}

	initFormModel() {
		super.initFormModel();
		this.formModel.addField('name', {
			getValue: () => this.state.stateForm.name,
			validation: {
				required: true,
				rules:
					this.state.stateForm.operation?.name === RuleOperation.Add.name &&
					[
						{
							assert: () => this.state.stateForm.name?.length <= 74,
							message: 'Name cannot be longer than 74 characters.',
						},
						{
							assert: () => !this.state.stateForm.name?.includes(' '),
							message: 'Name cannot contain spaces.',
						},
						{
							assert: () => /^[a-zA-Z](?:[a-zA-Z0-9_.-]*[a-zA-Z0-9])?$/.test(this.state.stateForm.name ?? ''),
							message: 'Name must begin with a letter, end with a letter or number, and may contain only letters, numbers, underscores, periods or dashes.',
						}
					]
			}
		});

		this.formModel.addField('trafficDirection', {
			getValue: () => this.props.porDirection === PortOpeningRequestsDirection.SubscriptionToSubscription
				? new TrafficDirection().fromValue(1) : this.state.stateForm.trafficDirection,
			validation: {
				required: this.props.porDirection === PortOpeningRequestsDirection.SubscriptionToOnPrem,
			},
		});

		this.formModel.addField('protocol', {
			getValue: () => this.state.stateForm.protocol,
			validation: {
				required: true
			},
		});

		this.formModel.fields.destinationPorts.validation.rules.push(
			this.PortInputValidationRule({
				minimumPort: 0
			})
		);

		this.formModel.addField('description', {
			getValue: () => this.state.stateForm.description
		});
	}

	// eslint-disable-next-line class-methods-use-this
	getDefaultFormState() {
		return {
			isTemporary: false,
			expirationDate: null,
			operation: RuleOperation.Add,
			action: RuleAction.Allow,
			name: '',
			trafficDirection: null,
			protocol: 'Any'
		};
	}

	resetIPsValidations(){
		this.formModel.fields.sourceIPs.validation.rules = [];
		this.formModel.fields.destinationIPs.validation.rules = [];
	}

	initTrafficDirectionValidations(trafficDirectionValue: number) {
		//0 = In ; 1 == Out
		this.resetIPsValidations();
		if (trafficDirectionValue == 1) {
			this.formModel.fields.sourceIPs.validation.rules.push(
				this.IpInputValidationRule({
					onlyPrivateAddresses: true
				}),
				this.IpIsInAddressSpacesRule()
			);
			this.formModel.fields.destinationIPs.validation.rules.push(
				this.IpInputValidationRule({
					onlyPrivateAddresses: true
				})
			);
		} else {
			this.formModel.fields.sourceIPs.validation.rules.push(
				this.IpInputValidationRule({
					onlyPrivateAddresses: true
				})
			);
			this.formModel.fields.destinationIPs.validation.rules.push(
				this.IpInputValidationRule({
					onlyPrivateAddresses: true
				}),
				this.IpIsInAddressSpacesRule()
			);
		}
	}

	handleTrafficDirection(event){
		this.handleStateFormChange('trafficDirection', new TrafficDirection().fromValue(event.target.value));
		this.initTrafficDirectionValidations(event.target.value);
	}

	getSourceIPOptions() {
		const subnets = this.props.validVirtualNetworks.flatMap(vNet => vNet.properties.subnets);
		const addressSpaces = subnets
			.flatMap(subnet => subnet.properties.addressPrefixes ?? subnet.properties.addressPrefix);
		return addressSpaces;
	}

	getDestinationIPOptions = () => {
		if (!this.formCanBeEdited()) return [];
		const subnets = this.props.vNetsToSpoke?.flatMap(vNet => vNet.properties.subnets);
		const addressSpaces = subnets?.flatMap(subnet => subnet.properties.addressPrefixes ?? subnet.properties.addressPrefix);
		return addressSpaces;
	};

	wasEditted() {
		if(this.props.editingRule){
			return (
				this.props.editingRule.description != this.stateFormHandler().description.value
				//|| this.props.editingRule.isTemporary != this.stateFormHandler().isTemporary.value
				|| this.props.editingRule.action.value != this.stateFormHandler().action.value			
				|| this.props.editingRule.sourceIPs != this.stateFormHandler().sourceIPs.value
				|| this.props.editingRule.destinationIPs != this.stateFormHandler().destinationIPs.value
				|| this.props.editingRule.destinationPorts != this.stateFormHandler().destinationPorts.value
				|| this.props.editingRule.trafficDirection != this.state.stateForm.trafficDirection
				|| this.props.editingRule.protocol != this.state.stateForm.protocol
				|| (this.props.editingRule.operation.value == RuleOperation.Add.value && (this.props.editingRule.name != this.state.stateForm.name))
			);}
		return true;
	}

	render() {
		return (
			<Modal show={this.props.visible}>
				<Modal.Window className="ruleModal">
					<Modal.Window.Header>
						<Modal.Window.Header.Title>
							{this.props.isReadOnly ? 'View Rule' : !this.isModalForNewRule() ? 'Edit Rule' : 'New Rule'}
						</Modal.Window.Header.Title>
						<Modal.Window.Header.CloseButton onClick={this.props.onClose}>
							<XFilledIcon size='small' />
						</Modal.Window.Header.CloseButton>
					</Modal.Window.Header>
					<Modal.Window.Body>
						{ !this.isModalForNewRule() &&
							<ToggleField
								{...this.stateFormHandler().operation}								
								label="Operation"
								disabled={this.props.isReadOnly}
								options={this.props.isReadOnly ?
									[
										new RuleOperation().fromValue(this.stateFormHandler().operation.value)
									]									
									:!this.stateFormHandler().isTemporary.value ?
										[
											RuleOperation.Modify,
											RuleOperation.Remove
										]:
										[
											RuleOperation.Remove
										]
								}
							/>
						}
						<Grid variant="4-up">
							<Grid.Item>
								<ToggleField
									{...this.stateFormHandler().isTemporary}
									value={this.state.stateForm.isTemporary?.toString()}
									options= {(this.formCanBeEdited())? [
									/*{text: 'Temporary', value: 'true'},*/
										{text: 'Permanent', value: 'false'},
									]
										:this.state.stateForm.isTemporary?
											[
												{text: 'Temporary', value: 'true'}]
											:
											[{text: 'Permanent', value: 'false'},
											]}
									label="Type"
									disabled={this.state.stateForm.operation?.name !== RuleOperation.Add.name || !this.formCanBeEdited()}
								/>
							</Grid.Item>
							<Grid.Item>
								<DatePickerField style={{visibility: this.stateFormHandler().expirationDate.value ? 'visible' : 'hidden'}}
									{...this.stateFormHandler().expirationDate}											
									value={ this.stateFormHandler().expirationDate.value ? moment(this.stateFormHandler().expirationDate.value).format('MM-YYYY-DD'):''}
									disabled= {true}
								/>
							</Grid.Item>
						</Grid>
						<Grid variant="2-up">
							<Grid.Item>	
								<TextInputField
									label="Rule Name"
									value={this.state.stateForm.name}
									validation={this.formModel.fields.name.validation}
									onChange={
										(event) => {
											this.handleStateFormChange('name', event.target.value);
										}
									}
									disabled={this.state.stateForm.operation?.name !== RuleOperation.Add.name || !this.formCanBeEdited()}
								/>
							</Grid.Item>
							<Grid.Item>
								<SelectField
									{...this.stateFormHandler().region}
									options={new RuleRegion().listByDirection(PortOpeningRequestsDirection.SubscriptionToOnPrem, this.props.validVirtualNetworks)}
									disabled={this.state.stateForm.operation?.name !== RuleOperation.Add.name || !this.formCanBeEdited()}
								/>
							</Grid.Item>
						</Grid>
						<>
							<Grid variant="2-up">
								<Grid.Item>
									<ToggleField
										{...this.stateFormHandler().action}
										options= { (this.formCanBeEdited())? [
											RuleAction.Allow,
											RuleAction.Block,
										]:this.stateFormHandler().action?.value?
											[RuleAction.Allow]
											:
											[RuleAction.Block]
										}
										label="Action"
										disabled={!this.formCanBeEdited()}
									/>
								</Grid.Item>
								{this.props.porDirection === PortOpeningRequestsDirection.SubscriptionToOnPrem ?
									<Grid.Item>
										<SelectField
											label="Traffic Direction"
											value={this.state.stateForm.trafficDirection?.value}
											options={[
												{text: 'Inbound (XOMNetwork -> Subscription)', value: TrafficDirection.In.value},
												{text: 'Outbound (Subscription -> XOMNetwork)', value: TrafficDirection.Out.value},
											]}
											className="full-width"
											onChange={(event) => this.handleTrafficDirection(event)}
											validation={this.formModel.fields.trafficDirection.validation}
											disabled={!this.formCanBeEdited()}
										/>
									</Grid.Item>
									:
									<Grid.Item/>
								}
							</Grid>
							<Grid variant="2-up">
								{this.props.porDirection === PortOpeningRequestsDirection.SubscriptionToOnPrem &&
									<>
										<Grid.Item>
											<TextAreaField
												{...this.stateFormHandler().sourceIPs}
												label="Source (IPs / IP Range / CIDR)"
												disabled={!this.formCanBeEdited()}
											/>
										</Grid.Item>
										<Grid.Item>
											<TextAreaField
												{...this.stateFormHandler().destinationIPs}
												label="Destination (IPs / IP Range / CIDR)"
												disabled={!this.formCanBeEdited()}
											/>
										</Grid.Item>
									</>}
								{this.props.porDirection === PortOpeningRequestsDirection.SubscriptionToSubscription &&
									<>
										<Grid.Item>
										  {this.formCanBeEdited() ? (
											<MultipleSelectField
												{...this.stateFormHandler().sourceIPs}
												options={this.getSourceIPOptions()}
												label="Source (IPs / IP Range / CIDR)"
												note='Multiple values are allowed.' />

										  ) : (
											<TextInputField
												label="Source (IPs / IP Range / CIDR)"
													value={this.stateFormHandler().sourceIPs.value}
													disabled="true"
													note='Multiple values are allowed.' 
											/>
										 )}
										</Grid.Item>
										<Grid.Item>
										  {this.formCanBeEdited() ? (
											<MultipleSelectField
												{...this.stateFormHandler().destinationIPs}
												options={this.getDestinationIPOptions()}
												label="Destination (IPs / IP Range / CIDR)"
												note='Multiple values are allowed.' />
										  ) : (
											<TextInputField
												label="Destination (IPs / IP Range / CIDR)"
												value={this.stateFormHandler().destinationIPs.value}
													disabled="true"
													note='Multiple values are allowed.'
											/>
										  )}
										</Grid.Item>
									</>}
							</Grid>
							<Grid variant="2-up">
								<Grid.Item>
									<TextAreaField
										{...this.stateFormHandler().destinationPorts}
										label="Destination Ports"
										disabled={!this.formCanBeEdited()}
									/>
								</Grid.Item>
								<Grid.Item />
							</Grid>
							<Grid variant="2-up">
								<Grid.Item>
									<TextAreaField
										{...this.stateFormHandler().description}
										label="Description"
										disabled={!this.formCanBeEdited()}
									/>
								</Grid.Item>
								<Grid.Item>
									<SelectField
										label="Protocol"
										value={this.state.stateForm.protocol}
										options={['Any', 'TCP', 'UDP', 'ICMP']}
										onChange={(event) => this.handleStateFormChange('protocol', event.target.value)}
										validation={this.formModel.fields.protocol.validation}
										disabled={!this.formCanBeEdited()}
									/>
								</Grid.Item>
							</Grid>
						</>
					</Modal.Window.Body>
					{ !this.props.isReadOnly &&
						<Modal.Window.Footer>
							<Grid variant="2-up">
								<Grid.Item>
									<Button
										variant="primary"
										disabled={
											(!this.formModel.isValid() 
												|| !this.wasEditted()) 
												&& this.state.stateForm.operation.name !== RuleOperation.Remove.name
										}
										onClick={this.handleRequest}
									>
										{this.props.editingRule ? 'Confirm' : 'Create'}
									</Button>
								</Grid.Item>
								<Grid.Item>
									<Button
										variant="secondary"
										onClick={this.handleCancel}
									>
										Cancel
									</Button>
								</Grid.Item>
							</Grid>
						</Modal.Window.Footer>
					}
				</Modal.Window>
			</Modal>
		);
	}
}